import React, { useEffect, useState } from 'react';
import { PageToolbar } from 'components/PageToolbar';
import {
  Button,
  Divider,
  Grid,
  Link,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { useHistory, useParams, generatePath } from 'react-router';
import { ApplicationState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { dashboardActions } from '../actions/dashboardActions';

import { AuthRoutes } from 'shared/Routes';
import CountCard from 'features/dashboard/CountCard';
import PageSpinner from 'components/spinners/PageSpinner';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import FilterListIcon from '@material-ui/icons/FilterList';

import VolumeCard from 'features/dashboard/VolumeCard';
import {
  DateRangePickerComponent,
  now,
  lastMonth,
} from 'components/DateRangePicker';
import { getAuthorizedDepartments } from 'helpers/authorizedDepartments';
import {
  ResolutionEnumDto,
  StackDateTypeDto,
  StackStateEnumDto,
  StackStatisticsQueryRequest,
  StackStatisticsResponseBin,
} from '.generated/api';
import MultiSelectFilter from 'components/MultiSelectFilter';
import RangeFilter from 'components/RangeFilter';
import { Autocomplete } from 'components/Autocomplete';
import FilterDrawer from 'components/FilterDrawer';
import {
  TranslatedFilterOptions,
  translateFilterOptions,
} from 'helpers/filterOptions';
import { sawmillActions } from 'actions/sawmillActions';

type Datasets = {
  dates: Date[];
  pregistration: number[];
  fullMeasurement: number[];
  pieceCount: number[];
  assignedToSawmill: number[];
  volume: number[];
};

function unzipBins(bins: StackStatisticsResponseBin[]) {
  const datasets: Datasets = {
    dates: [],
    pregistration: [],
    fullMeasurement: [],
    pieceCount: [],
    assignedToSawmill: [],
    volume: [],
  };

  for (const bin of bins) {
    datasets.dates.push(new Date(bin.date));
    datasets.pregistration.push(bin.preregistrations);
    datasets.fullMeasurement.push(bin.fullMeasurements);
    datasets.pieceCount.push(bin.pieceCount);
    datasets.assignedToSawmill.push(bin.assignedToSawmill);
    datasets.volume.push(bin.volume);
  }

  return datasets;
}

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    flex: 1,
    height: 10,
    overflow: 'auto',
  },
  datePickerItem: {
    width: '25%',
    minWidth: '14rem',
  },
  filterContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  selectContainer: {
    maxWidth: '100%', // ensures ellipsis in select dropdown
  },
  countCard: {
    minWidth: '25%',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
  },
  volume: {
    height: '100%',
    marginTop: theme.spacing(2),
    minHeight: '20rem',
  },
  filterButtonRoot: {
    margin: 0,
    minWidth: 0,
    padding: 14,
  },
  filtersApplied: {
    marginLeft: theme.spacing(2),
  },
}));

const CountCardGrid: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const classes = useStyles();
  return (
    <Grid item xs className={classes.countCard}>
      {children}
    </Grid>
  );
};

const getResolutionFromDateRange = (from: Date, to: Date) => {
  const daysBetween = (to.getTime() - from.getTime()) / (1000 * 3600 * 24);
  let resolution;
  if (daysBetween <= 1) {
    resolution = ResolutionEnumDto.HOURLY;
  } else if (daysBetween <= 30) {
    resolution = ResolutionEnumDto.DAILY;
  } else {
    resolution = ResolutionEnumDto.MONTHLY;
  }
  return resolution;
};

interface ParamTypes {
  orgId: string;
  deptId: string;
}

const Dashboard: React.FC = () => {
  const { orgId, deptId } = useParams<ParamTypes>();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);

  const [filters, setFilters] = useState<StackStatisticsQueryRequest>({
    departmentIds: [],
    from: lastMonth().toISOString(),
    to: now().toISOString(),
    resolution: ResolutionEnumDto.MONTHLY,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [datasets, setDatasets] = useState<Datasets | undefined>(undefined);

  const [translatedFilterOptions, setTranslatedFilterOptions] =
    useState<TranslatedFilterOptions>({});

  const [extendedFiltersVisible, setExtendedFiltersVisible] = useState(false);

  const userAccess = useSelector(
    (state: ApplicationState) => state.isLoggedIn?.userAccess,
  );

  const orgDepartments = useSelector(
    (state: ApplicationState) => state.organization?.organization?.departments,
  );

  const sawmills = useSelector(
    (state: ApplicationState) => state.sawmills?.sawmills ?? [],
  );

  // const [stackStatistics, setMockData] = useState(
  //   generateMockData(filters.resolution),
  // );
  const { measurementCard, stackStatistics, filterOptions, filtersApplied } =
    useSelector((state: ApplicationState) => state.dashboard);

  const getFromFilterOrDefault = () => {
    return filters.from ?? lastMonth().toISOString();
  };

  const getToFilterOrDefault = () => {
    return filters.to ?? now().toISOString();
  };

  const handleResetFilters = () => {
    setFilters((filters) => ({
      departmentIds: filters.departmentIds,
      from: filters.from,
      to: filters.to,
      resolution: filters.resolution,
      timeZone: filters.timeZone,
    }));
  };

  const handleAreaNumberSelect = (value?: number) => {
    setFilters((filters) => ({
      ...filters,
      areaNumber: value,
    }));
  };

  const handleAssortmentLengthChange = (value: number[]) => {
    if (
      value[0] === filterOptions?.assortmentLengthMin &&
      value[1] === filterOptions.assortmentLengthMax
    ) {
      setFilters((filters) => ({
        ...filters,
        assortmentLengthMin: undefined,
        assortmentLengthMax: undefined,
      }));
    } else {
      setFilters((filters) => ({
        ...filters,
        assortmentLengthMin: value[0],
        assortmentLengthMax: value[1],
      }));
    }
  };

  const handleDateRangeChange = (
    from: Date | null,
    to: Date | null,
    valid: boolean,
  ) => {
    if (from && to && valid) {
      const resolution = getResolutionFromDateRange(from, to);
      setFilters((filters) => ({
        ...filters,
        from: from.toISOString(),
        to: to.toISOString(),
        resolution,
      }));
    }
  };

  // fetch measurement card
  useEffect(() => {
    if (orgId) {
      dispatch(sawmillActions.getSawmillsByOrganizationId(orgId));
      dispatch(dashboardActions.getOrganizationMeasurementCard(orgId));
    }
  }, [orgId]);

  // translate filter options
  useEffect(() => {
    if (measurementCard && filterOptions) {
      setTranslatedFilterOptions(
        translateFilterOptions(measurementCard, filterOptions),
      );
    } else {
      setTranslatedFilterOptions({});
    }
  }, [measurementCard, filterOptions]);

  // department/all to organization url
  useEffect(() => {
    if (
      deptId === 'all' &&
      (userAccess?.organizations?.find(
        (organization) => organization.id === orgId,
      ) ||
        userAccess?.isGlobalAdmin)
    ) {
      history.replace(
        generatePath(AuthRoutes.Dashboard, {
          orgId: orgId,
        }),
      );
    }
  });

  // select departments
  useEffect(() => {
    if (userAccess && orgDepartments && deptId !== 'all') {
      if (deptId) {
        setFilters((filters) => ({
          ...filters,
          departmentIds: deptId ? [deptId] : [],
        }));
      } else {
        const departments = getAuthorizedDepartments(
          orgId,
          userAccess,
          orgDepartments,
          userAccess.departments,
        );

        setFilters((filters) => ({
          ...filters,
          departmentIds:
            departments?.map((department) => department.departmentId) ?? [],
        }));
      }
    }
  }, [deptId, userAccess, orgDepartments]);

  useEffect(() => {
    dispatch(dashboardActions.getStackStatistics(filters));
  }, [filters]);

  useEffect(() => {
    setDatasets(stackStatistics ? unzipBins(stackStatistics.bins) : undefined);
  }, [stackStatistics]);

  // link to stack overview with query parameters derived from filters
  const StacksLink: React.FC<{
    label: string;
    params?: Map<string, string>;
  }> = ({ label, params }) => {
    const query = new URLSearchParams();
    let key: keyof typeof filters;
    for (key in filters) {
      if (
        key === 'departmentIds' ||
        key === 'timeZone' ||
        key === 'resolution'
      ) {
        continue;
      }
      const filter = filters[key];
      if (Array.isArray(filter)) {
        for (const value of filter) {
          query.append(key, value);
        }
      } else {
        query.set(key, filters[key] as string);
      }
    }

    params?.forEach((paramValue, paramKey) => {
      query.set(paramKey, paramValue);
    });

    return (
      <Link
        href={`${generatePath(AuthRoutes.Stacks, {
          orgId: orgId,
          deptId: deptId ?? 'all',
        })}?${query.toString()}`}
        className={classes.link}
      >
        {label} <ArrowForwardIcon />
      </Link>
    );
  };

  return (
    <>
      <PageToolbar>
        <Typography variant="subtitle1">{t('dashboard_page.title')}</Typography>
        <Divider />
        <div className={classes.filterContainer}>
          <Grid container spacing={2}>
            <DateRangePickerComponent
              itemClass={classes.datePickerItem}
              clearable
              startDate={new Date(getFromFilterOrDefault())}
              endDate={new Date(getToFilterOrDefault())}
              onChange={handleDateRangeChange}
            />
            <Grid item>
              <Button
                classes={{ root: classes.filterButtonRoot }}
                size="large"
                variant={extendedFiltersVisible ? 'contained' : 'outlined'}
                color="secondary"
                onClick={() =>
                  setExtendedFiltersVisible(!extendedFiltersVisible)
                }
              >
                <FilterListIcon />
              </Button>
              {!!filtersApplied && (
                <span className={classes.filtersApplied}>
                  {t('stacks_page.n_filters_applied', {
                    count: filtersApplied,
                  })}
                </span>
              )}
            </Grid>
          </Grid>
        </div>
      </PageToolbar>

      <div className={classes.pageContainer}>
        <FilterDrawer
          open={extendedFiltersVisible}
          onResetFilters={handleResetFilters}
          onClose={() => {
            setExtendedFiltersVisible(false);
          }}
        >
          <Grid container spacing={2} direction="column">
            {translatedFilterOptions.surveyors && (
              <Grid item className={classes.selectContainer}>
                <MultiSelectFilter
                  onSelect={(values) => {
                    setFilters((filters) => ({
                      ...filters,
                      surveyors: values.length > 0 ? values : undefined,
                    }));
                  }}
                  values={filters.surveyors ?? undefined}
                  label={translatedFilterOptions.surveyors.title}
                  items={translatedFilterOptions.surveyors.options}
                />
              </Grid>
            )}

            {translatedFilterOptions.treeSpecies && (
              <Grid item className={classes.selectContainer}>
                <MultiSelectFilter
                  onSelect={(values) => {
                    setFilters((filters) => ({
                      ...filters,
                      treeSpecies: values.length > 0 ? values : undefined,
                    }));
                  }}
                  values={filters.treeSpecies ?? undefined}
                  label={translatedFilterOptions.treeSpecies.title}
                  items={translatedFilterOptions.treeSpecies.options}
                />
              </Grid>
            )}
            {translatedFilterOptions.assortments && (
              <Grid item className={classes.selectContainer}>
                <MultiSelectFilter
                  onSelect={(values) => {
                    setFilters((filters) => ({
                      ...filters,
                      assortments: values.length > 0 ? values : undefined,
                    }));
                  }}
                  values={filters.assortments ?? undefined}
                  label={translatedFilterOptions.assortments.title}
                  items={translatedFilterOptions.assortments.options}
                />
              </Grid>
            )}

            <Grid item className={classes.selectContainer}>
              <MultiSelectFilter
                onSelect={(values) => {
                  setFilters((filters) => ({
                    ...filters,
                    sawmillIds: values.length > 0 ? values : undefined,
                  }));
                }}
                values={filters?.sawmillIds ?? undefined}
                label={t('stacks_page.sawmill')}
                items={sawmills.map((sawmill) => ({
                  label: sawmill.name,
                  value: sawmill.id,
                }))}
              />
            </Grid>

            {translatedFilterOptions.areaNumber && (
              <Grid item className={classes.selectContainer}>
                <Autocomplete
                  label={translatedFilterOptions.areaNumber.title}
                  // options={[12345, 1, 2, 3, 4, 5, 123, 1234]}
                  options={filterOptions?.areaNumber ?? []}
                  getOptionLabel={(option: number) => option.toString()}
                  onSelect={handleAreaNumberSelect}
                  value={
                    filters.areaNumber != null ? filters.areaNumber : undefined
                  }
                />
              </Grid>
            )}
            {translatedFilterOptions.assortmentLength && (
              <Grid item className={classes.selectContainer}>
                <RangeFilter
                  disabledText={t('stacks_page.no_lengths_to_filter_by')}
                  label={translatedFilterOptions.assortmentLength.title}
                  value={
                    filters.assortmentLengthMin != null &&
                    filters.assortmentLengthMax != null
                      ? [
                          filters.assortmentLengthMin,
                          filters.assortmentLengthMax,
                        ]
                      : undefined
                  }
                  min={
                    filterOptions?.assortmentLengthMin != null
                      ? filterOptions.assortmentLengthMin
                      : undefined
                  }
                  max={
                    filterOptions?.assortmentLengthMax != null
                      ? filterOptions.assortmentLengthMax
                      : undefined
                  }
                  // min={0}
                  // max={10}
                  step={0.01}
                  onChange={handleAssortmentLengthChange}
                />
              </Grid>
            )}
          </Grid>
        </FilterDrawer>
        <PageSpinner
          isLoading={
            measurementCard === undefined ||
            stackStatistics === undefined ||
            filterOptions === undefined
          }
        >
          {stackStatistics && (
            <>
              <Grid container spacing={2} direction="row">
                <CountCardGrid>
                  <CountCard
                    dates={datasets?.dates ?? []}
                    minDate={new Date(getFromFilterOrDefault())}
                    maxDate={new Date(getToFilterOrDefault())}
                    resolution={filters.resolution}
                    title={t('dashboard_page.preregistrations')}
                    count={stackStatistics.preregistrations}
                    values={datasets?.pregistration ?? []}
                  >
                    <StacksLink
                      label={t('dashboard_page.see_preregistrations')}
                      params={
                        new Map([
                          [
                            'dateType',
                            StackDateTypeDto.PRE_REGISTRATION.toString(),
                          ],
                          [
                            'states',
                            StackStateEnumDto.PRE_REGISTRATION.toString(),
                          ],
                        ])
                      }
                    />
                  </CountCard>
                </CountCardGrid>

                <CountCardGrid>
                  <CountCard
                    dates={datasets?.dates ?? []}
                    minDate={new Date(getFromFilterOrDefault())}
                    maxDate={new Date(getToFilterOrDefault())}
                    resolution={filters.resolution}
                    title={t('dashboard_page.full_measurements')}
                    count={stackStatistics.fullMeasurements}
                    values={datasets?.fullMeasurement ?? []}
                  >
                    <StacksLink
                      params={
                        new Map([
                          ['dateType', StackDateTypeDto.MEASURED.toString()],
                          ['fullMeasurement', 'true'],
                        ])
                      }
                      label={t('dashboard_page.see_full_measurements')}
                    />
                  </CountCard>
                </CountCardGrid>

                <CountCardGrid>
                  <CountCard
                    dates={datasets?.dates ?? []}
                    minDate={new Date(getFromFilterOrDefault())}
                    maxDate={new Date(getToFilterOrDefault())}
                    resolution={filters.resolution}
                    title={t('dashboard_page.piece_counts')}
                    count={stackStatistics.pieceCount}
                    values={datasets?.pieceCount ?? []}
                  >
                    <StacksLink
                      params={
                        new Map([
                          ['dateType', StackDateTypeDto.MEASURED.toString()],
                          ['fullMeasurement', 'false'],
                        ])
                      }
                      label={t('dashboard_page.see_piece_counts')}
                    />
                  </CountCard>
                </CountCardGrid>

                <CountCardGrid>
                  <CountCard
                    dates={datasets?.dates ?? []}
                    minDate={new Date(getFromFilterOrDefault())}
                    maxDate={new Date(getToFilterOrDefault())}
                    resolution={filters.resolution}
                    title={t('dashboard_page.assigned')}
                    count={stackStatistics.assignedToSawmill}
                    values={datasets?.assignedToSawmill ?? []}
                  >
                    <StacksLink
                      params={
                        new Map([
                          [
                            'dateType',
                            StackDateTypeDto.ASSIGNED_TO_SAWMILL.toString(),
                          ],
                          [
                            'states',
                            StackStateEnumDto.ASSIGNED_TO_SAWMILL.toString(),
                          ],
                        ])
                      }
                      label={t('dashboard_page.see_assigned')}
                    />
                  </CountCard>
                </CountCardGrid>
              </Grid>
              <VolumeCard
                dates={datasets?.dates ?? []}
                minDate={new Date(getFromFilterOrDefault())}
                maxDate={new Date(getToFilterOrDefault())}
                values={datasets?.volume ?? []}
                className={classes.volume}
                resolution={filters.resolution}
              />
            </>
          )}
        </PageSpinner>
      </div>
    </>
  );
};

export { Dashboard };
